import _ from "lodash";

const fieldStateHelper = {
  /**
   * Return field disable status
   *
   * @param {string} validationRules
   * @param {boolean} isDisabled
   * @param {object} editedEntry
   */
  getDisabledStatus(validationRules, isDisabled, editedEntry) {
    if (isDisabled) {
      return true;
    }

    const disabledConditionFieldName = getConditionFieldName(
      validationRules,
      "disabled_if"
    );
    const disabledConditionFieldValues = getConditionFieldValues(
      validationRules,
      "disabled_if"
    );
    const enabledConditionFieldName = getConditionFieldName(
      validationRules,
      "enabled_if"
    );
    const enabledConditionFieldValues = getConditionFieldValues(
      validationRules,
      "enabled_if"
    );

    if (disabledConditionFieldName && disabledConditionFieldValues) {
      let newValue = _.get(editedEntry, `fields.${disabledConditionFieldName}`);
      let conditionStatus = getConditionStatus(
        newValue,
        disabledConditionFieldValues
      );
      if (conditionStatus !== -1) {
        return true;
      }
      return false;
    }

    if (enabledConditionFieldName && enabledConditionFieldValues) {
      let newValue = _.get(editedEntry, `fields.${enabledConditionFieldName}`);
      let conditionStatus = getConditionStatus(
        newValue,
        enabledConditionFieldValues
      );

      if (conditionStatus !== -1) {
        return false;
      }
      return true;
    }

    return false;
  },

  /**
   * Return field hide status
   *
   * @param {string} validationRules
   * @param {boolean} isHidden
   * @param {object} editedEntry
   */
  getHiddenStatus(validationRules, isHidden, editedEntry) {
    const visibleConditionFieldName = getConditionFieldName(
      validationRules,
      "visible_if"
    );

    if (isHidden && !visibleConditionFieldName) {
      return true;
    }

    const hiddenConditionFieldName = getConditionFieldName(
      validationRules,
      "hidden_if"
    );

    const hiddenConditionFieldValues = getConditionFieldValues(
      validationRules,
      "hidden_if"
    );

    const visibleConditionFieldValues = getConditionFieldValues(
      validationRules,
      "visible_if"
    );

    let conditionStatus = null;

    if (hiddenConditionFieldName && hiddenConditionFieldValues) {
      let newValue = _.get(editedEntry, `fields.${hiddenConditionFieldName}`);
      conditionStatus = getConditionStatus(
        newValue,
        hiddenConditionFieldValues
      );

      if (conditionStatus !== -1) {
        return true;
      }

      return false;
    }

    if (visibleConditionFieldName && visibleConditionFieldValues) {
      let newValue = _.get(editedEntry, `fields.${visibleConditionFieldName}`);
      conditionStatus = getConditionStatus(
        newValue,
        visibleConditionFieldValues
      );

      if (conditionStatus !== -1) {
        return false;
      }
      return true;
    }

    return false;
  },
};

/**
 * Parse validation rules and fetch condition field name
 *
 * @param {string} validationRules
 * @param {string} condition
 */
function getConditionFieldName(validationRules, condition) {
  if (!validationRules) {
    return null;
  }

  let validationRule = parseValidationRule(validationRules, condition);

  let arrayHandler = validationRule.split(":");
  if (arrayHandler[0] !== condition || !arrayHandler[1]) {
    return null;
  }
  return arrayHandler[1].split(",")[0];
}

/**
 * Parse validation rules and fetch condition values
 *
 * @param {string} validationRules
 * @param {string} condition
 */
function getConditionFieldValues(validationRules, condition) {
  if (!validationRules) {
    return null;
  }

  let validationRule = parseValidationRule(validationRules, condition);

  let arrayHandler = validationRule.split(":");
  if (arrayHandler[0] !== condition || !arrayHandler[1]) {
    return null;
  }
  let values = [];
  arrayHandler = arrayHandler[1].split(",");
  for (let i = 0; i < arrayHandler.length; i++) {
    const element = arrayHandler[i];
    if (i !== 0) {
      values.push(element);
    }
  }
  return values;
}

/**
 * parse specific rule from all validation rules
 *
 * @param {string} validationRules
 * @param {string} condition
 */
function parseValidationRule(validationRules, condition) {
  let regex = new RegExp("(" + condition + ":.+)\\||" + condition + ":.+");
  let regexMatchingResults = validationRules.match(regex);
  if (!regexMatchingResults) {
    return "";
  }
  return regexMatchingResults[1]
    ? regexMatchingResults[1]
    : regexMatchingResults[0];
}

/**
 * return value type in more descriptive form
 *
 * @param {any} value
 */
function getType(value) {
  let type = typeof value;
  if (value === null) {
    return "null";
  }
  if (type === "object") {
    if (value.hasOwnProperty("length")) {
      type =
        value.length > 0 && typeof value[0] === "object"
          ? "arrayOfObjects"
          : "arrayOfInteger";
    }
  }
  return type;
}

/**
 * Check if condition is satisfied
 *
 * @param {any} newValue
 * @param {array} conditionFieldValues
 */
function getConditionStatus(newValue, conditionFieldValues) {
  let typeOfNewValue = getType(newValue);
  let index = -1;
  const booleanValue = newValue ? "true" : "false";

  switch (typeOfNewValue) {
    case "boolean":
      index = conditionFieldValues.findIndex((value) => value === booleanValue);
      break;
    case "number":
      const numberIndex = conditionFieldValues.findIndex(
        (value) => Number(value) === newValue
      );
      const booleanIndex = conditionFieldValues.findIndex(
        (value) => value === booleanValue
      );

      index = numberIndex === -1 && booleanIndex === -1 ? -1 : 0;

      break;
    case "object":
      index = conditionFieldValues.findIndex(
        (value) => Number(value) === Number(_.get(newValue, "system.id"))
      );

      break;
    case "arrayOfInteger":
      for (let i = 0; i < newValue.length; i++) {
        const element = newValue[i];
        if (
          conditionFieldValues.findIndex(
            (value) => Number(value) === Number(element)
          ) !== -1
        ) {
          index = 1;
          break;
        }
      }
      break;
    case "arrayOfObjects":
      index = -1;
      for (let i = 0; i < newValue.length; i++) {
        const element = newValue[i];
        if (
          conditionFieldValues.findIndex(
            (value) => Number(value) === Number(_.get(element, "system.id"))
          ) !== -1
        ) {
          index = 1;
          break;
        }
      }
      break;
    default:
      index = conditionFieldValues.findIndex((value) => value === newValue);
  }
  return index;
}

export default fieldStateHelper;
